import { isCourseFree } from "@repo/shared";
import { useTranslation } from "react-i18next";

import { useAvailableCourses } from "~/api/queries/useAvailableCourses";
import { resolveDirectEnrollHref } from "~/utils/courseLinks";

import type { SupportedLanguages } from "@repo/shared";

/**
 * El cierre de la landing. Replanteo 2026-07-31:
 *
 * - Se cayó la segunda salida ("Pre-inscribirme a los que vienen −80%") y la línea de
 *   urgencia del 80%: la pre-inscripción está oculta en la Home, así que el cierre
 *   prometía un descuento que acá no se puede tomar. Queda UN solo CTA, que es lo que
 *   corresponde al final de la página.
 * - El mensaje ya no promete plazos ("en un mes…"): los cursos son asincrónicos y cada
 *   uno dura lo que dura. Ahora cierra con lo único cierto y chico: el primer paso.
 * - Las pruebas se acortaron y van `whitespace-nowrap` para que NUNCA se partan en dos
 *   líneas en desktop, que era como se venían viendo.
 */
export function FinalCtaLaunch() {
  const { t, i18n } = useTranslation();

  // El CTA lleva al alta real del curso gratis, igual que el hero y el curso destacado.
  const { data: courses } = useAvailableCourses({ language: i18n.language as SupportedLanguages });
  const featured = courses?.find((c) => c.isFeatured) ?? courses?.find(isCourseFree);
  const ctaHref = featured ? resolveDirectEnrollHref({ slug: featured.slug }) : "#cursos";

  return (
    // Opaca igual que `QuotesSection`, que es la que va justo arriba: si una fuera crema al
    // 70% y la otra sólida, entre las dos aparecería el mismo escalón de tres niveles que se
    // acaba de sacar de la costura del rasgado.
    // Desktop: el cierre + footer llenan el viewport completo al scrollear al fondo
    // (sin que asome la sección anterior): min-h = 100svh − footer (~330px), contenido
    // centrado verticalmente. En mobile el alto natural alcanza.
    <section className="landing-grain relative flex items-center bg-landing-cream px-5 py-20 text-center sm:px-8 sm:py-24 lg:min-h-[calc(100svh-330px)]">
      <div className="relative z-10 mx-auto max-w-[820px]">
        <h2
          className="font-display text-[clamp(26px,3.8vw,42px)] font-extrabold leading-[1.1] tracking-tight text-landing-text"
          style={{ textWrap: "balance" }}
        >
          {t("landing.launch.fin.headlinePre")}
          <em className="not-italic text-landing-red">{t("landing.launch.fin.headlineEm")}</em>
          {t("landing.launch.fin.headlinePost")}
        </h2>
        {/* Entre el título y el botón no va nada más (replanteo 2026-08-20): el "no
            tenés que decidir hoy / dura menos que el scroll" sonaba a gimmick y estiraba
            el cierre. El título hace la promesa; el botón la cumple. La única línea de
            acompañamiento va DEBAJO del costo de entrada, como remate. */}
        <div className="mt-8">
          <a
            href={ctaHref}
            data-cuelume-press
            data-cuelume-release
            className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-landing-red px-8 py-4 text-[16px] font-bold text-white shadow-landing-red transition-all duration-150 hover:-translate-y-0.5 hover:bg-landing-red-hover hover:shadow-landing-red-hover sm:w-auto"
          >
            {t("landing.launch.fin.cta1")} →
          </a>
          <p className="mt-3 text-[13.5px] text-landing-muted">{t("landing.launch.fin.sub")}</p>
          <p className="mx-auto mt-6 max-w-[46ch] text-pretty text-[15px] italic leading-snug text-landing-muted">
            {t("landing.launch.fin.kicker")}
          </p>
        </div>
      </div>
    </section>
  );
}
